<template>
  <v-app>
    <v-container fluid>
      <v-row>
        <v-col cols="12">
          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title color="blue-grey-lighten-4" class="panel-header">
                Asset Disposal/Write-off Records
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-row class="py-2 justify-items-between">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      single-line
                      hide-details
                      density="compact"
                      variant="outlined"
                      bg-color="grey-lighten-4"
                      class="pl-2"
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="4" cols="12">
                    <div class="d-flex align-center flex-column">
                      <v-btn-group
                        variant="outlined"
                        density="compact"
                        size="x-small"
                        divided
                      >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="success"
                          @click="exportToCSV"
                          >CSV</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="primary"
                          @click="exportToExcel"
                          >EXCEL</v-btn
                        >
                        <v-btn
                          prepend-icon="mdi mdi-download"
                          variant="flat"
                          color="red"
                          @click="exportToPDF"
                          >PDF</v-btn
                        >
                      </v-btn-group>
                    </div>
                  </v-col>

                  <v-col md="4" cols="12">
                    <div class="text-right pr-2">
                      <v-btn
                        v-if="$insertAccess($route.fullPath)"
                        @click="goToAdd()"
                        color="primary"
                        variant="tonal"
                        prepend-icon="mdi mdi-plus"
                        rounded="xl"
                      >
                        Add New Disposal/Write-off
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
                <v-data-table
                  :headers="headers"
                  :items="getSchema"
                  :loading="loading"
                  v-model:items-per-page="itemsPerPage"
                  :search="search"
                  :hover="true"
                  density="compact"
                >
                  <template v-slot:item.disposal_date="{ item }">
                    {{ formattedDate(item.disposal_date) }}
                  </template>
                  <template v-slot:item.customer="{ item }">
                    <span v-if="item.customer">
                      <v-chip
                        color="blue"
                        size="small"
                        variant="tonal"
                        prepend-icon="mdi-account"
                      >
                        {{ item.customer.company_name || item.customer.company_owner_name || 'N/A' }}
                      </v-chip>
                    </span>
                    <span v-else class="text-grey">N/A</span>
                  </template>
                  <template v-slot:item.reference_no="{ item }">
                    <span class="text-body-2">{{ item.reference_no || "N/A" }}</span>
                  </template>
                  <template v-slot:item.details="{ item }">
                    <v-chip
                      color="red"
                      size="small"
                      variant="tonal"
                      >{{ item.details?.length || 0 }} Asset(s)</v-chip
                    >
                  </template>
                  <template v-slot:item.status="{ item }">
                    <v-chip
                      :color="item.status === 0 ? 'grey' : 'green'"
                      size="small"
                      variant="flat"
                      
                    >
                      {{ formatStatus(item.status) }}
                    </v-chip>
                  </template>
                  <template v-slot:item.created_by="{ item }">
                    <span class="text-body-2">{{ item.created_by?.full_name || "N/A" }}</span>
                  </template>

                  <template v-slot:item.approval_status_display="{ item }">
                    <v-chip
                    v-if="item.approval_current_process && item.approval_current_process.status === 'Pending'"
                    color="yellow-darken-3"
                    size="small"
                    >
                        Wating for {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>

                    <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                    color="green"
                    size="small"
                    >
                        Approved By {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>
                    
                    <v-chip
                    v-else-if="item.approval_current_process && item.approval_current_process.status === 'Approved'"
                    color="red"
                    size="small"
                    >
                        Cancelled By  {{ item.approval_current_process.approval_step_name.name }}
                    </v-chip>
                    <v-chip
                    v-else
                    color="green"
                    size="small"
                    >
                    {{ item.approval_status }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <div class="d-flex align-center justify-center" style="gap: 6px;">
                      <v-btn
                        icon="mdi-pencil"
                        size="small"
                        variant="text"
                        color="orange"
                        @click="editItem(item)"
                        v-if="$updateAccess($route.fullPath) && canEdit(item)"
                      />
                      <v-btn
                        icon="mdi-file-document-outline"
                        size="small"
                        variant="text"
                        color="primary"
                        @click="viewItem(item)"
                        v-if="$viewAccess($route.fullPath)"
                      />
                    </div>
                  </template>
                </v-data-table>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>
    </v-container>
  </v-app>
</template>

<script>
import { useStore } from "vuex";
import { ref } from "vue";
import { toast } from "vue3-toastify";
import formattedDate from "@/plugins/dateFormatter";

export default {
  components: {},
  data: () => ({
    itemsPerPage: "10",
    panel: [0],
    search: "",
    headers: [
      { title: "#SL", align: "center", key: "id", width: "80px", sortable: false },
      { title: "Code", key: "code", align: "center", sortable: false, width: "120px" },
      { title: "Disposal Date", key: "disposal_date", align: "center", sortable: false, width: "130px" },
      { title: "Customer/Buyer", key: "customer", align: "center", sortable: false, width: "200px" },
      { title: "Reference No", key: "reference_no", align: "center", sortable: false, width: "150px" },
      { title: "Assets Disposed", key: "details", align: "center", sortable: false, width: "150px" },
      { title: "Status", key: "status", align: "center", sortable: false, width: "120px" },
      { title: "Created By", key: "created_by", align: "center", sortable: false, width: "150px" },
      { title: "Actions", align: "center", key: "actions", sortable: false, width: "120px" },
      { title: "Approval Status", align: "center", key: "approval_status_display", sortable: true },
    ],
  }),
  watch: {},
  created() {
    this.loadAssetDisposals();
  },
  computed: {
    getSchema() {
      return this.$store.getters.getAssetDisposalList;
    },
  },
  setup() {
    const errorMessages = ref("");
    const loading = ref(false);
    const store = useStore();

    return {
      errorMessages,
      loading,
    };
  },
  methods: {
    formattedDate,
    loadAssetDisposals() {
      this.loading = true;
      this.$store
        .dispatch("getAssetDisposalList", {})
        .then((response) => {
          this.loading = false;
        })
        .catch((error) => {
          this.loading = false;
          toast.error(error.response?.data?.message || "Failed to load asset disposals");
        });
    },
    goToAdd() {
      this.$store.commit("UNSET_EDITING_ASSET_DISPOSAL");
      this.$router.push({ name: "add-disposal-write-off" });
    },
    editItem(item) {
      this.$store.commit("SET_EDITING_ASSET_DISPOSAL", item);
      this.$router.push({ name: "add-disposal-write-off" });
    },
     canEdit(item){
      return (!item.approval_status || item.approval_status.toLowerCase() == 'pending' || item.status == 0)
    },
    viewItem(item) {
      const raw = item && item.raw ? item.raw : item;
      const id = raw?.id;
      if (!id) return;
      this.$router.push({ name: "disposal-write-off-record-details", params: { id } });
    },
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      const num = parseFloat(value);
      return num >= 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
    },
    exportToPDF() {
      let serialNumber = 1;
      const columns = ["#SL", "Code", "Disposal Date", "Customer", "Reference No", "Assets", "Status", "Created By"];
      const rows = this.getSchema.map((item) => [
        serialNumber++,
        item.code,
        this.formattedDate(item.disposal_date),
        item.customer ? (item.customer.company_name || item.customer.company_owner_name || "N/A") : "N/A",
        item.reference_no || "N/A",
        item.details?.length || 0,
        this.formatStatus(item.status),
        item.created_by?.full_name || "N/A",
      ]);
      this.$exportTableToPDF(columns, rows, "Asset Disposal List", "asset_disposal_list_" + this.$fileDate());
    },
    exportToExcel() {
      var data = [["#SL", "Code", "Disposal Date", "Customer", "Reference No", "Assets", "Status", "Created By"]];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          this.formattedDate(item.disposal_date),
          item.customer ? (item.customer.company_name || item.customer.company_owner_name || "N/A") : "N/A",
          item.reference_no || "N/A",
          item.details?.length || 0,
          this.formatStatus(item.status),
          item.created_by?.full_name || "N/A",
        ]);
      });
      this.$exportTableToExcel(data, "asset_disposal_list_" + this.$fileDate());
    },
    exportToCSV() {
      var data = [["#SL", "Code", "Disposal Date", "Customer", "Reference No", "Assets", "Status", "Created By"]];
      var serialNumber = 1;
      this.getSchema.forEach((item) => {
        data.push([
          serialNumber++,
          item.code,
          this.formattedDate(item.disposal_date),
          item.customer ? (item.customer.company_name || item.customer.company_owner_name || "N/A") : "N/A",
          item.reference_no || "N/A",
          item.details?.length || 0,
          this.formatStatus(item.status),
          item.created_by?.full_name || "N/A",
        ]);
      });
      this.$exportTableToCSV(data, "asset_disposal_list_" + this.$fileDate());
    },
    formatStatus(status) {
      if (status === 0) return "Draft";
      if (status === 1) return "Submitted";
      return "Unknown";
    },
  },
};
</script>

<style scoped>
.panel-header {
  font-weight: 600;
}

:deep(.v-data-table__td) {
  text-align: center;
}
</style>

